/**
 * LoadingPlaybackControls — v2 Compact
 *
 * Barre de contrôle de lecture ultra-compacte, premium et professionnelle.
 * Design : ligne unique, glassmorphism, micro-animations, empreinte minimale.
 *
 * Positionnée en bas-centre du viewport 3D.
 */

import React, { useCallback, useState, useRef, useEffect } from 'react'
import type { PlaybackSpeed } from '../../hooks/useLoadingPlayback'

// ── Icons (inline SVG) ────────────────────────────────────────────────────────

const IconPlay = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 2.5a.5.5 0 0 1 .765-.424l10 5.5a.5.5 0 0 1 0 .848l-10 5.5A.5.5 0 0 1 3 13.5v-11Z" />
  </svg>
)

const IconPause = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 3a1 1 0 0 1 2 0v10a1 1 0 0 1-2 0V3Zm6 0a1 1 0 0 1 2 0v10a1 1 0 0 1-2 0V3Z" />
  </svg>
)

const IconReset = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1Z" />
    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466Z" />
  </svg>
)

const IconPrev = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M.5 3.5A.5.5 0 0 1 1 4v3.248l6.267-3.636c.52-.302 1.233.043 1.233.696v2.94l6.267-3.636c.52-.302 1.233.043 1.233.696v7.384c0 .653-.713.998-1.233.696L8.5 8.752v2.94c0 .653-.713.998-1.233.696L1 8.752V12a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5Z" />
  </svg>
)

const IconNext = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M15.5 3.5a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V8.752l-6.267 3.636c-.52.302-1.233-.043-1.233-.696v-2.94l-6.267 3.636C.713 12.69 0 12.345 0 11.692V4.308c0-.653.713-.998 1.233-.696L7.5 7.248v-2.94c0-.653.713-.998 1.233-.696L15 7.248V4a.5.5 0 0 1 .5-.5Z" />
  </svg>
)

const IconGrip = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-6 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-6 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
  </svg>
)

const IconBox = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5 8.186 1.113zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923l6.5 2.6z" />
  </svg>
)

// ── Props ─────────────────────────────────────────────────────────────────────

export interface LoadingPlaybackControlsProps {
  step: number
  totalSteps: number
  isPlaying: boolean
  speed: PlaybackSpeed
  isAtEnd: boolean
  isAtStart: boolean
  progress: number
  currentItemLabel?: string
  onPlay: () => void
  onPause: () => void
  onReset: () => void
  onNext: () => void
  onPrev: () => void
  onSpeedChange: (s: PlaybackSpeed) => void
  onSeek: (n: number) => void
  isDark: boolean
  fullscreen?: boolean
}

const SPEEDS: PlaybackSpeed[] = [0.5, 1, 2, 5]

// ── Status config ─────────────────────────────────────────────────────────────

function getStatus(isPlaying: boolean, isAtEnd: boolean, isAtStart: boolean) {
  if (isPlaying)  return { label: 'Lecture', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)', dot: true }
  if (isAtEnd)    return { label: 'Terminé', color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  dot: false }
  if (isAtStart)  return { label: 'Prêt',    color: '#64748b', bg: 'rgba(100,116,139,0.12)', dot: false }
  return           { label: 'Pause',   color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  dot: false }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LoadingPlaybackControls({
  step,
  totalSteps,
  isPlaying,
  speed,
  isAtEnd,
  isAtStart,
  progress,
  currentItemLabel,
  onPlay,
  onPause,
  onReset,
  onNext,
  onPrev,
  onSpeedChange,
  onSeek,
  isDark,
  fullscreen = false,
}: LoadingPlaybackControlsProps) {

  const [hoveredSpeed, setHoveredSpeed] = useState<PlaybackSpeed | null>(null)
  const [position, setPosition] = useState({ x: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStartPos = useRef({ x: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const handlePlayPause = useCallback(() => {
    if (isPlaying) onPause(); else onPlay()
  }, [isPlaying, onPlay, onPause])

  const handleScrub = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onSeek(Number(e.target.value)),
    [onSeek]
  )

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return // Only left click
    setIsDragging(true)
    dragStartPos.current.x = e.clientX - position.x
    e.preventDefault()
  }, [position.x])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return
    const newX = e.clientX - dragStartPos.current.x
    const baseLeft = Math.max(16, window.innerWidth / 2 - 500)
    const controlWidth = containerRef.current?.getBoundingClientRect().width ?? 0
    const minOffset = 16 - baseLeft
    const maxOffset = window.innerWidth - controlWidth - 16 - baseLeft
    const constrainedX = Math.max(minOffset, Math.min(maxOffset, newX))
    setPosition({ x: constrainedX })
  }, [isDragging])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // ── Design tokens ──────────────────────────────────────────────────────────

  const ACCENT      = '#3b82f6'
  const ACCENT_GLOW = 'rgba(59,130,246,0.30)'
  const glass       = isDark ? 'rgba(6,8,20,0.90)' : 'rgba(248,250,255,0.94)'
  const border      = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'
  const textPrimary = isDark ? '#f1f5f9' : '#0f172a'
  const textMuted   = isDark ? '#475569' : '#94a3b8'
  const divider     = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'

  const status = getStatus(isPlaying, isAtEnd, isAtStart)

  // ── Icon button helper ─────────────────────────────────────────────────────

  const iconBtn = (
    onClick: () => void,
    children: React.ReactNode,
    opts: { disabled?: boolean; title?: string; accent?: boolean } = {}
  ) => {
    const disabled = opts.disabled ?? false
    const accent   = opts.accent ?? false
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={opts.title}
        aria-label={opts.title}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: accent ? 32 : 26,
          height: accent ? 32 : 26,
          borderRadius: accent ? 8 : 6,
          border: `1px solid ${accent
            ? (isPlaying ? 'rgba(245,158,11,0.4)' : `${ACCENT}44`)
            : border}`,
          background: accent
            ? (isPlaying
              ? 'linear-gradient(135deg,#f59e0b,#d97706)'
              : `linear-gradient(135deg,${ACCENT},#1d4ed8)`)
            : 'transparent',
          color: accent ? '#fff' : (disabled ? textMuted : textPrimary),
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.3 : 1,
          transition: 'all 0.14s ease',
          flexShrink: 0,
          boxShadow: accent
            ? (isPlaying ? '0 2px 10px rgba(245,158,11,0.4)' : `0 2px 10px ${ACCENT_GLOW}`)
            : 'none',
        }}
      >
        {children}
      </button>
    )
  }

  // ── Scrubber track ─────────────────────────────────────────────────────────

  const scrubberFill = isAtEnd
    ? 'linear-gradient(90deg,#22c55e,#16a34a)'
    : `linear-gradient(90deg,${ACCENT},#818cf8)`

  return (
    <>
      {/* ── Inline keyframes ── */}
      <style>{`
        @keyframes lpc-pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.35; transform:scale(0.6); }
        }
        @keyframes lpc-glow {
          0%,100% { box-shadow: 0 0 0 2px ${ACCENT_GLOW}; }
          50%      { box-shadow: 0 0 0 4px rgba(59,130,246,0.12); }
        }
        .lpc-ibtn:hover { opacity: 0.85 !important; }
        .lpc-spdbtn:hover { border-color: ${ACCENT}55 !important; color: ${isDark ? '#93c5fd' : ACCENT} !important; }
      `}</style>

      {/* ── Wrapper ── */}
      <div
        ref={containerRef}
        className="top-[190px] lg:top-[160px] 2xl:top-[104px]"
        style={{
          position: fullscreen ? 'fixed' : 'absolute',
          left: `calc(max(16px, 50% - 500px) + ${position.x}px)`,
          transform: 'translateX(0)',
          zIndex: fullscreen ? 70 : 55,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          userSelect: 'none',
          pointerEvents: 'auto',
        }}
      >

        {/* ── Item label tooltip (above bar) ── */}
        {currentItemLabel && step > 0 && step <= totalSteps && !isAtEnd && (
          <div style={{
            padding: '3px 10px',
            borderRadius: 99,
            background: isDark ? 'rgba(6,8,20,0.82)' : 'rgba(248,250,255,0.92)',
            border: `1px solid ${border}`,
            backdropFilter: 'blur(12px)',
            fontSize: 10,
            fontWeight: 600,
            color: isDark ? '#94a3b8' : '#475569',
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: '0.01em',
          }}>
            <span style={{ color: isDark ? '#60a5fa' : ACCENT, marginRight: 5 }}>▸</span>
            {currentItemLabel}
          </div>
        )}

        {/* ── Main compact bar ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            background: glass,
            border: `1px solid ${border}`,
            borderRadius: 14,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: isDark
              ? `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03), 0 2px 8px ${ACCENT_GLOW}`
              : '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
            overflow: 'hidden',
            animation: isPlaying ? 'lpc-glow 2s ease-in-out infinite' : 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
        >

          {/* ── Drag handle ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 10px',
            height: 40,
            borderRight: `1px solid ${divider}`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}>
            <span style={{ color: textMuted, opacity: 0.6 }}>
              <IconGrip size={12} />
            </span>
          </div>

          {/* ── Icon + Label ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 14px',
            height: 40,
            borderRight: `1px solid ${divider}`,
            minWidth: 110,
          }}>
            <div style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              background: `linear-gradient(135deg,${ACCENT}22,#1d4ed822)`,
              border: `1px solid ${ACCENT}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconBox size={10} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: isDark ? '#60a5fa' : ACCENT,
                whiteSpace: 'nowrap',
              }}>
                Chargement
              </span>
              <span style={{
                fontSize: 8,
                fontWeight: 500,
                color: textMuted,
                letterSpacing: '0.02em',
              }}>
                Playback
              </span>
            </div>
          </div>

          {/* ── Scrubber section ── */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            padding: '0 14px',
            height: 40,
            width: 160,
            borderRight: `1px solid ${divider}`,
          }}>
            {/* Track */}
            <div style={{ position: 'relative', height: 16, display: 'flex', alignItems: 'center' }}>
              {/* Background track */}
              <div style={{
                position: 'absolute',
                left: 0, right: 0,
                height: 4,
                borderRadius: 99,
                background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: scrubberFill,
                  borderRadius: 99,
                  transition: 'width 0.28s ease',
                  boxShadow: `0 0 6px ${ACCENT_GLOW}`,
                }} />
              </div>
              {/* Interactive range */}
              <input
                type="range"
                min={0}
                max={totalSteps}
                value={step}
                onChange={handleScrub}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  margin: 0, padding: 0,
                }}
                aria-label={`Étape ${step} / ${totalSteps}`}
              />
            </div>
            {/* Step counter */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: textMuted, fontWeight: 600 }}>0</span>
              <span style={{
                fontSize: 10,
                fontWeight: 800,
                color: textPrimary,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
              }}>
                {step}
                <span style={{ color: textMuted, fontWeight: 500 }}>/{totalSteps}</span>
              </span>
              <span style={{ fontSize: 9, color: textMuted, fontWeight: 600 }}>{totalSteps}</span>
            </div>
          </div>

          {/* ── Transport controls ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 12px',
            height: 40,
            borderRight: `1px solid ${divider}`,
          }}>
            {iconBtn(onReset, <IconReset size={12} />, { title: 'Réinitialiser (R)' })}
            {iconBtn(onPrev,  <IconPrev size={12}  />, { title: 'Précédent (←)', disabled: isAtStart })}
            {iconBtn(handlePlayPause, isPlaying ? <IconPause size={13} /> : <IconPlay size={13} />, {
              title: isPlaying ? 'Pause (Espace)' : 'Lecture (Espace)',
              disabled: isAtEnd && !isPlaying,
              accent: true,
            })}
            {iconBtn(onNext,  <IconNext size={12}  />, { title: 'Suivant (→)', disabled: isAtEnd })}
          </div>

          {/* ── Status badge ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 12px',
            height: 40,
            borderRight: `1px solid ${divider}`,
            minWidth: 80,
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              borderRadius: 99,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              background: status.bg,
              border: `1px solid ${status.color}33`,
              color: status.color,
              whiteSpace: 'nowrap',
            }}>
              {status.dot && (
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: status.color,
                  animation: 'lpc-pulse 1.1s ease-in-out infinite',
                  flexShrink: 0,
                }} />
              )}
              {status.label}
            </span>
          </div>

          {/* ── Speed selector ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            padding: '0 12px',
            height: 40,
          }}>
            <span style={{
              fontSize: 9,
              color: textMuted,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginRight: 3,
              whiteSpace: 'nowrap',
            }}>×</span>
            {SPEEDS.map(s => {
              const active = s === speed
              return (
                <button
                  key={s}
                  className="lpc-spdbtn"
                  onClick={() => onSpeedChange(s)}
                  onMouseEnter={() => setHoveredSpeed(s)}
                  onMouseLeave={() => setHoveredSpeed(null)}
                  title={`Vitesse ${s}×`}
                  aria-label={`Vitesse ${s}×`}
                  aria-pressed={active}
                  style={{
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: active
                      ? `1px solid ${ACCENT}55`
                      : `1px solid ${divider}`,
                    background: active
                      ? `linear-gradient(135deg,${ACCENT}1a,#1d4ed81a)`
                      : 'transparent',
                    color: active
                      ? (isDark ? '#93c5fd' : ACCENT)
                      : (hoveredSpeed === s ? (isDark ? '#94a3b8' : '#475569') : textMuted),
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    cursor: 'pointer',
                    transition: 'all 0.12s ease',
                    flexShrink: 0,
                  }}
                >
                  {s}
                </button>
              )
            })}
          </div>

        </div>
      </div>
    </>
  )
}
